import {
  CheckCircle,
  InfoOutlined,
  Warning,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { amber, blue, green, red } from '@mui/material/colors';
import React, { PropsWithChildren } from 'react';
import Row from '../Layout/Row';

type Severity = 'success' | 'error' | 'warning' | 'info';

const iconColorMap: Record<Severity, string> = {
  success: green[500],
  error: red[500],
  warning: amber[500],
  info: blue[500],
};

const StyledRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<{ ownerState: { severity: Severity } }>(({ theme, ownerState }) => {
  const colorMap: Record<Severity, { bg: string }> = {
    success: {
      bg: green[50],
    },
    error: {
      bg: red[50],
    },
    warning: {
      bg: amber[50],
    },
    info: {
      bg: blue[50],
    },
  };

  return {
    padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.md),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: colorMap[ownerState.severity].bg,
  };
});

const iconMap: Record<Severity, React.ElementType> = {
  success: CheckCircle,
  error: Warning,
  warning: WarningAmberOutlined,
  info: InfoOutlined,
};

type Props = {
  severity: Severity;
  iconPosition?: 'start' | 'end';
};

const Alert: React.FC<PropsWithChildren<Props>> = ({
  severity,
  children,
  iconPosition = 'end',
}) => {
  const Icon = iconMap[severity];

  return (
    <StyledRow ownerState={{ severity }} spacing="xs" justify="center" centered>
      {iconPosition === 'start' && (
        <Icon fontSize="small" sx={{ color: iconColorMap[severity] }} />
      )}
      <Typography variant="h6">{children}</Typography>
      {iconPosition === 'end' && (
        <Icon fontSize="small" sx={{ color: iconColorMap[severity] }} />
      )}
    </StyledRow>
  );
};

export default Alert;
